import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { wristInCm, wristInRange, displayWrist } from './kd-wrist-utils.js';

/**
 * @extends {Component<{sizeInput: HTMLInputElement, unitInput: HTMLSelectElement, fitInput: HTMLSelectElement, normalizedInput: HTMLInputElement, guide: HTMLDialogElement, guideTrigger: HTMLButtonElement, sizeError: HTMLParagraphElement}>}
 */
class KdWristSizing extends Component {
  requiredRefs = ['sizeInput', 'unitInput', 'fitInput', 'normalizedInput', 'guide', 'guideTrigger', 'sizeError'];
  #abort;
  #unit = 'cm';
  #guideOwner;
  #focusFit = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  #bind() {
    this.#abort?.abort();
    if (this.#guideOwner) unlockScroll(this.#guideOwner);
    this.#guideOwner = this.refs.guide;
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#unit = this.refs.unitInput.value;
    this.addEventListener('input', this.#input, options);
    this.addEventListener('change', this.#change, options);
    this.addEventListener('click', this.#click, options);
    this.addEventListener('keydown', this.#key, options);
    this.refs.guide.addEventListener('close', this.#closed, options);
    this.refs.sizeInput.addEventListener('invalid', () => this.#validate(true), options);
    this.closest('form')?.addEventListener('submit', this.#submit, { ...options, capture: true });
    this.#validate(false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.#guideOwner) unlockScroll(this.#guideOwner);
    super.disconnectedCallback();
  }

  #validate(showError) {
    const { sizeInput, normalizedInput, sizeError, unitInput } = this.refs;
    const valid = wristInRange(sizeInput.value, unitInput.value, Number(this.dataset.min), Number(this.dataset.max));
    const message = valid ? '' : this.dataset.rangeMessage;
    sizeInput.setCustomValidity(message);
    normalizedInput.value = valid ? String(wristInCm(sizeInput.value, unitInput.value)) : '';
    sizeInput.setAttribute('aria-invalid', String(showError && !valid));
    sizeError.hidden = !showError || valid;
    return valid;
  }

  #input = (event) => {
    if (event.target === this.refs.sizeInput) this.#validate(true);
  };

  #change = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target === this.refs.unitInput) {
      const cm = wristInCm(this.refs.sizeInput.value, this.#unit);
      this.#unit = this.refs.unitInput.value;
      const { sizeInput } = this.refs;
      sizeInput.min = displayWrist(Number(this.dataset.min), this.#unit);
      sizeInput.max = displayWrist(Number(this.dataset.max), this.#unit);
      sizeInput.placeholder = this.#unit === 'cm' ? '17.5' : '6.89';
      if (cm !== null) sizeInput.value = displayWrist(cm, this.#unit);
      this.#validate(sizeInput.value !== '');
    }
    if (event.target.matches('[data-guide-unit]')) {
      const unit = event.target.value;
      this.querySelectorAll('[data-guide-cm]').forEach((cell) => {
        cell.textContent = `${displayWrist(Number(cell.dataset.guideCm), unit)} ${unit === 'cm' ? 'cm' : 'in'}`;
      });
    }
  };

  #submit = (event) => {
    if (!this.#validate(true) || !this.refs.fitInput.checkValidity()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.#validate(true)) this.refs.sizeInput.reportValidity();
      else this.refs.fitInput.reportValidity();
    }
  };

  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('button');
    if (target === this.refs.guideTrigger) {
      this.refs.guide.showModal();
      lockScroll(this.refs.guide);
      this.refs.guideTrigger.setAttribute('aria-expanded', 'true');
    } else if (target?.hasAttribute('data-guide-close')) {
      this.refs.guide.close();
    } else if (target?.hasAttribute('data-guide-tab')) {
      this.#selectTab(target);
    } else if (target?.hasAttribute('data-choose-size')) {
      const cm = Number(target.dataset.chooseSize);
      this.refs.sizeInput.value = displayWrist(cm, this.refs.unitInput.value);
      this.#validate(true);
      this.#focusFit = true;
      this.refs.guide.close();
    } else if (event.target === this.refs.guide) {
      const rect = this.refs.guide.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        this.refs.guide.close();
      }
    }
  };

  #closed = () => {
    unlockScroll(this.refs.guide);
    this.refs.guideTrigger.setAttribute('aria-expanded', 'false');
    (this.#focusFit ? this.refs.fitInput : this.refs.guideTrigger).focus();
    this.#focusFit = false;
  };

  #selectTab(tab) {
    this.querySelectorAll('[data-guide-tab]').forEach((item) => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    this.querySelectorAll('[data-guide-panel]').forEach((panel) => {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
    });
  }

  #key = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.matches('[data-guide-tab]')) return;
    const tabs = [...this.querySelectorAll('[data-guide-tab]')];
    const current = tabs.indexOf(event.target);
    const next = { ArrowRight: (current + 1) % tabs.length, ArrowLeft: (current + tabs.length - 1) % tabs.length, Home: 0, End: tabs.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.#selectTab(tabs[next]);
    tabs[next].focus();
  };
}

if (!customElements.get('kd-wrist-sizing')) customElements.define('kd-wrist-sizing', KdWristSizing);
